import { useCallback, useState } from "react";
import { usePageLoader } from "../../../../common/contexts/page_content_context";
import { useCreateDebounceHandler, useDebouncedCallback } from "../../../../common/hooks/use_debounced_callback";
import { useNavigate } from "react-router";
import { processError } from "../../../../common/utils/base_error";
import { evaluateParamValue } from "../../../../common/utils/params_utils";
import { useDependencyInjection } from "../../../../config/di/dependency_injection_context";
import { DropDown } from "../../../../common/domain/models/dropdown";
import { usePageStartBootstrap } from "../../../../common/hooks/use_page_start_bootstrap";
import { GridRowSelectionModel } from "@mui/x-data-grid-pro";
import { ContasPagarCopiaForm } from "../schemas/ContasPagar_lista/ContasPagar_copia";
import { initialContasPagarFiltersValues, useContasPagarListaFilters } from "../hooks/contas_pagar_lista/filters";
import { useContasPagarListaPagination } from "../hooks/contas_pagar_lista/pagination";
import { ContasPagarLista } from "../../domain/models/contas_pagar_lista";
import { ContasPagamentoForm } from "../schemas/ContasPagar_lista/ContasPagar_pagamento";
import { useReportViewer } from "../../../../config/hooks/utils/report_viewer";
import { ContasPagarAnaliticoFilter } from "../../domain/models/contas_pagar_analitico_filter";
import { ReportContasPagarAnaliticoColumnsForm, ReportContasPagarAnaliticoForm } from "../schemas/reports/contasPagarAnaliticoReport";
import { ValoresContasMes } from "../../../comercial/domain/models/valores_contas_mes";

type LoadContasPagarListaState = {

    idsCentroCusto?: number[];
    idsOrdemCompra?: number[];
    idsBanco?: number[];
    idsSubconta?: number[];
    idsFornecedor?: number[];
    idsFuncionario?: number[];
    idsFuncionarioPago?: number[];
    idsProdutoServico?: number[];
    idsPgtoMedicao?: number[];

    titulo?: string | null;
    valor?: number | null;
    nrContasPagar?: number | null;
    nrNotaFiscal?: string | null;
    nrNotaFiscalEspecial?: string | null;
    formaPagamento?: string | null;
    status?: number | null;

    dataPagarDe?: Date | null;
    dataPagarAte?: Date | null;
    dataPagoDe?: Date | null;
    dataPagoAte?: Date | null;
    dataCriacaoDe?: Date | null;
    dataCriacaoAte?: Date | null;

    page?: number;
    rowsPerPage?: number;

}


export const useContasPagarListaViewModel = () => {

    const {
        reports: {
            getReportContaPagarUseCase,
            getReportContasPagarAnaliticoUseCase,

        },
        financeiro: {
            copiaContasPagarUseCase,
            getAllContasPagarUseCase,
            pagamentoContasUseCase,
            deleteContasPagarUseCase,
            getContasPagarAnaliticoFiltrosUseCase,
            saveContasPagarAnaliticoFiltrosUseCase,

        },
        commons: {
            getCentroCustoDropDownUseCase,
            getBancosPagamentoDropDownUseCase,
            getOrdemComprasDropDownUseCase,
            getSubcontasDropDownUseCase,
            getFornecedorDropDownUseCase,
            getFuncionariosDropDownUseCase,
            getMedicaoTeceirizacaoDropDownUseCase,
            getProdutoServicoDropDownUseCase

        },

    } = useDependencyInjection();

    const { pushErrorNotification, pushWarnNotification, pushInfoNotification, setPageLoading } = usePageLoader();
    const [reportViewerState, startReportViewerProcess] = useReportViewer();

    const debouncedRef = useCreateDebounceHandler(1500);

    const [rowSelectionModel, setRowSelectionModel] = useState<GridRowSelectionModel>();

    const [openDeleteModal, setOpenDeleteModal] = useState(false);
    const [selectedIdContasPagar, setSelectedIdContasPagar] = useState<number>();

    const [openCadastroPagamentoModal, setOpenCadastroPagamentoModal] = useState(false);
    const [valorTotalRecebido, setValorTotalRecebido] = useState(0);

    const [openCopiaContasPagarModal, setOpenCopiaContasPagarModal] = useState(false);
    const [selectedIdContaPagarCopia, setSelectedIdContaPagarCopia] = useState<number>();
    const [tituloContasPagarCopia, settituloContasPagarCopia] = useState<string>();

    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);

    const [errorMessage, setErrorMessage] = useState<string>();
    const [contasPagarLista, setContasPagarLista] = useState<ContasPagarLista[]>();

    const [valoresContasMes, setValoresContasMes] = useState<ValoresContasMes | undefined>();

    const filters = useContasPagarListaFilters();
    const pagination = useContasPagarListaPagination();


    const [openReportContasPagarAnaliticoModal, setOpenReportContasPagarAnaliticoModal] = useState(false);
    const [filtroContasPagarAnalitico, setFiltroContasPagarAnalitico] = useState<ContasPagarAnaliticoFilter>();

    //DROPDOWN ESTADOS

    const [ddlCentroCusto, setDdlCentroCusto] = useState<DropDown[]>();
    const [ddlBanco, setDdlBanco] = useState<DropDown[]>();
    const [ddlOrdemCompra, setDdlOrdemCompra] = useState<DropDown[]>();
    const [ddlSubconta, setDdlSubconta] = useState<DropDown[]>();
    const [ddlFornecedor, setDdlFornecedor] = useState<DropDown[]>();
    const [ddlFuncionario, setDdlFuncionario] = useState<DropDown[]>();
    const [ddlProdutoServico, setDdlProdutoServico] = useState<DropDown[]>();
    const [ddlPgtoMedicao, setDdlPgtoMedicao] = useState<DropDown[]>();

    //#region CARREGAMENTO.

    const loadContasPagarListaState = useCallback(async (funcState?: LoadContasPagarListaState) => {
        try {
            const filteredTitulo = evaluateParamValue(funcState?.titulo, filters.titulo);
            const filteredValor = evaluateParamValue(funcState?.valor, filters.valor);
            const filteredNrContasPagar = evaluateParamValue(funcState?.nrContasPagar, filters.nrContasPagar);
            const filteredNrNotaFiscal = evaluateParamValue(funcState?.nrNotaFiscal, filters.nrNotaFiscal);
            const filteredNrNotaFiscalEspecial = evaluateParamValue(funcState?.nrNotaFiscalEspecial, filters.nrNotaFiscalEspecial);
            const filteredFormaPagamento = evaluateParamValue(funcState?.formaPagamento, filters.formaPagamento);
            const filteredStatus = evaluateParamValue(funcState?.status, filters.status);

            const filteredDataPagarDe = evaluateParamValue(funcState?.dataPagarDe, filters.dataPagarDe);
            const filteredDataPagarAte = evaluateParamValue(funcState?.dataPagarAte, filters.dataPagarAte);
            const filteredDataPagoDe = evaluateParamValue(funcState?.dataPagoDe, filters.dataPagoDe);
            const filteredDataPagoAte = evaluateParamValue(funcState?.dataPagoAte, filters.dataPagoAte);
            const filteredDataCriacaoDe = evaluateParamValue(funcState?.dataCriacaoDe, filters.dataCriacaoDe);
            const filteredDataCriacaoAte = evaluateParamValue(funcState?.dataCriacaoAte, filters.dataCriacaoAte);

            const filteredIdsCentroCusto = funcState?.idsCentroCusto ?? filters.idsCentroCusto;
            const filteredIdsOrdemCompra = funcState?.idsOrdemCompra ?? filters.idsOrdemCompra;
            const filteredIdsBanco = funcState?.idsBanco ?? filters.idsBanco;
            const filteredIdsSubconta = funcState?.idsSubconta ?? filters.idsSubconta;
            const filteredIdsFornecedor = funcState?.idsFornecedor ?? filters.idsFornecedor;
            const filteredIdsFuncionario = funcState?.idsFuncionario ?? filters.idsFuncionario;
            const filteredIdsFuncionarioPago = funcState?.idsFuncionarioPago ?? filters.idsFuncionarioPago;
            const filteredIdsProdutoServico = funcState?.idsProdutoServico ?? filters.idsProdutoServico;
            const filteredIdsPgtoMedicao = funcState?.idsPgtoMedicao ?? filters.idsPgtoMedicao;

            const filteredPage = funcState?.page ?? pagination.page;
            const filteredRowsPerPage = funcState?.rowsPerPage ?? pagination.rowsPerPage;

            const indicadores = await getAllContasPagarUseCase.getAllContasPagarLista(
                {
                    titulo: filteredTitulo ?? "",
                    valor: filteredValor,
                    nrContasPagar: filteredNrContasPagar,
                    nrNotaFiscal: filteredNrNotaFiscal,
                    nrNotaFiscalEspecial: filteredNrNotaFiscalEspecial,
                    formaPagamento: filteredFormaPagamento,
                    status: filteredStatus,

                    dataPagarDe: filteredDataPagarDe,
                    dataPagarAte: filteredDataPagarAte,
                    dataPagoDe: filteredDataPagoDe,
                    dataPagoAte: filteredDataPagoAte,
                    dataCriacaoDe: filteredDataCriacaoDe,
                    dataCriacaoAte: filteredDataCriacaoAte,

                    idsCentroCusto: filteredIdsCentroCusto,
                    idsOrdemCompra: filteredIdsOrdemCompra,
                    idsBanco: filteredIdsBanco,
                    idsSubconta: filteredIdsSubconta,
                    idsFornecedor: filteredIdsFornecedor,
                    idsFuncionario: filteredIdsFuncionario,
                    idsFuncionarioPago: filteredIdsFuncionarioPago,
                    idsProdutoServico: filteredIdsProdutoServico,
                    idsPgtoMedicao: filteredIdsPgtoMedicao,
                },
                filteredPage,
                filteredRowsPerPage
            );

            setContasPagarLista(getAllContasPagarUseCase.items);
            setValoresContasMes(indicadores);

            pagination.setRowsPerPage(getAllContasPagarUseCase.pageSize);
            pagination.setTotalItems(getAllContasPagarUseCase.totalItems);
        } catch (e: unknown) {
            setContasPagarLista([]);
            throw new Error("Falha ao buscar contas a pagar!");
        }
    }, [
        filters,
        pagination,
        getAllContasPagarUseCase,
    ]);


    const loadCentroCustoDropDown = useCallback(async () => {

        try {
            setPageLoading(true);
            const result = await getCentroCustoDropDownUseCase.getCentroCustoDropDown();
            setDdlCentroCusto(result);
        } catch (e: unknown) {
            pushErrorNotification('Error', processError(e, 'Falha ao buscar centro de custo!'));
        } finally {
            setPageLoading(false);
        }

    }, [getCentroCustoDropDownUseCase]);

    const loadBancosPagamentoDropDown = useCallback(async () => {
        try {
            setPageLoading(true);
            const result = await getBancosPagamentoDropDownUseCase.getBancosPagamentoDropDown();
            setDdlBanco(result);
        } catch (e: unknown) {
            pushErrorNotification('Error', processError(e, 'Falha ao buscar bancos!'));
        } finally {
            setPageLoading(false);
        }
    }, [getBancosPagamentoDropDownUseCase]);

    const loadOrdemCompraDropDown = useCallback(async () => {
        try {
            setPageLoading(true);
            const result = await getOrdemComprasDropDownUseCase.getOrdemComprasDropDown();
            setDdlOrdemCompra(result);
        } catch (e: unknown) {
            pushErrorNotification('Error', processError(e, 'Falha ao buscar ordens de compra!'));
        } finally {
            setPageLoading(false);
        }
    }, [getOrdemComprasDropDownUseCase]);

    const loadSubcontaDropDown = useCallback(async () => {
        try {
            setPageLoading(true);

            const result = await getSubcontasDropDownUseCase.getSubcontasDropDown();
            setDdlSubconta(result);
        } catch (e: unknown) {
            pushErrorNotification('Error', processError(e, 'Falha ao buscar subcontas!'));
        } finally {
            setPageLoading(false);
        }
    }, [getSubcontasDropDownUseCase]);


    const loadFornecedorDropDown = useCallback(async () => {
        try {
            setPageLoading(true);

            const result = await getFornecedorDropDownUseCase.getFornecedorDropDown();
            setDdlFornecedor(
                result.map((x) => ({
                    id: x.idFornecedor,
                    title: x.nomeFantasia
                }))
            );

        } catch (e: unknown) {
            pushErrorNotification('Error', processError(e, 'Falha ao buscar fornecedores!'));
        } finally {
            setPageLoading(false);
        }
    }, [getFornecedorDropDownUseCase]);

    const loadFuncionarioDropDown = useCallback(async () => {
        try {
            setPageLoading(true);

            const result = await getFuncionariosDropDownUseCase.getFuncionariosDropDown(undefined, true);
            setDdlFuncionario(result.map((x) => ({
                id: x.idFuncionario,
                title: x.nome,
            })));

        } catch (e: unknown) {
            pushErrorNotification('Error', processError(e, 'Falha ao buscar funcionários!'));
        } finally {
            setPageLoading(false);
        }
    }, [
        getFuncionariosDropDownUseCase,
        pushErrorNotification,
        setPageLoading
    ]);

    const loadProdutoServicoDropDown = useCallback(async () => {
        try {
            setPageLoading(true);

            const result = await getProdutoServicoDropDownUseCase.getProdutoServicoDropDown();
            setDdlProdutoServico(result);
        } catch (e: unknown) {
            pushErrorNotification('Error', processError(e, 'Falha ao buscar produtos/serviços!'));
        } finally {
            setPageLoading(false);
        }
    }, [getCentroCustoDropDownUseCase]);

    const loadPgtoMedicaoDropDown = useCallback(async () => {
        try {
            setPageLoading(true);

            const result = await getMedicaoTeceirizacaoDropDownUseCase.getMedicaoTeceirizacaoDropDown();
            setDdlPgtoMedicao(result);
        } catch (e: unknown) {
            pushErrorNotification('Error', processError(e, 'Falha ao buscar pagamentos de medição!'));
        } finally {
            setPageLoading(false);
        }
    }, [getCentroCustoDropDownUseCase]);



    //#endregion



    //#region START PAGE

    usePageStartBootstrap(async () => {
        try {
            if (contasPagarLista === undefined) {
                await loadContasPagarListaState();
                await loadCentroCustoDropDown();
                await loadBancosPagamentoDropDown();
                await loadOrdemCompraDropDown();
                await loadSubcontaDropDown();
                await loadFornecedorDropDown();
                await loadFuncionarioDropDown();
                await loadProdutoServicoDropDown();
                await loadPgtoMedicaoDropDown();

            }
        } catch (e: unknown) {
            setErrorMessage(processError(e, "Falha ao buscar lista de contas a pagar!")[0]);
        }
    })

    const handleContasPagarEdit = useCallback((idContasPagar: number | null) => {
        navigate('/ContasPagar_cadastro', { state: { idContasPagar } });
    }, [navigate]);

    const handleNewContasPagar = useCallback(() => {
        navigate("/ContasPagar_cadastro");
    }, [navigate]);


    //#endregion

    //#region FUNÇÕES RELATÓRIO


    const handleReportContasPagarAnaliticoSubmit = useCallback(async (form: ReportContasPagarAnaliticoForm) => {
        try {
            setLoading(true)
            await startReportViewerProcess({
                fetchCallback: async (mimeType) => {
                    return await getReportContasPagarAnaliticoUseCase.execute(
                        {
                            centroCusto: form.centroCusto,
                            subconta: form.subconta,
                            titulo: form.titulo,
                            fornecedor: form.fornecedor,
                            ordemCompra: form.ordemCompra,
                            dataPagarDe: form.dataPagarDe,
                            dataPagarAte: form.dataPagarAte,
                            dataPagoDe: form.dataPagoDe,
                            dataPagoAte: form.dataPagoAte,
                            status: form.status,
                            bancoPagamento: form.bancoPagamento,
                            tipoConta: form.tipoConta,
                            nro: form.nro,
                            notaFiscal: form.notaFiscal,
                            responsavel: form.responsavel,
                            funcionarioFolha: form.funcionarioFolha,
                            idUsuario: form.idUsuario,
                            medicao: form.medicao,
                            produtosServicos: form.produtosServicos,
                            formaPagamento: form.formaPagamento,
                            dataCriacaoDe: form.dataCriacaoDe,
                            dataCriacaoAte: form.dataCriacaoAte,
                            dataCompetenciaDe: form.dataCompetenciaDe,
                            dataCompetenciaAte: form.dataCompetenciaAte,
                            ordenacao: form.ordenacao,
                            colunas: form.colunas,
                            requiredMimeType: mimeType,
                        });
                }
            });

        } catch (e: unknown) {
            pushErrorNotification('Error', processError(e, 'Falha ao carregar filtros do relatório!'));

        } finally {
            setLoading(false);
        }
    }, [
        getReportContasPagarAnaliticoUseCase,
        pushErrorNotification,
        startReportViewerProcess
    ]);


    const handleReportContasPagarAnaliticoColumnsSubmit = useCallback(async (form: ReportContasPagarAnaliticoColumnsForm) => {
        try {
            setLoading(true)
            await saveContasPagarAnaliticoFiltrosUseCase.execute({
                nroContasPagar: form.nroContasPagar,
                centroCusto: form.centroCusto,
                ordemCompra: form.ordemCompra,
                fornecedor: form.fornecedor,
                bancoPagamento: form.bancoPagamento,
                nroDocumento: form.nroDocumento,
                responsavel: form.responsavel,
                status: form.status,
                titulo: form.titulo,
                subconta: form.subconta,
                dataPagar: form.dataPagar,
                dataPago: form.dataPago,
                valorPagar: form.valorPagar,
                valorPago: form.valorPago,
                contaBancaria: form.contaBancaria,
                observacao: form.observacao,
                notaFiscal: form.notaFiscal,
                notaFiscalEspecial: form.notaFiscalEspecial,
                bdi: form.bdi,
                bdi2: form.bdi2,
                nroMedicao: form.nroMedicao,
                totalBdi: form.totalBdi,
                totalBdi2: form.totalBdi2,
                formaPagamento: form.formaPagamento,
                informacaoGeral: form.informacaoGeral,
                dataCriacao: form.dataCriacao,
                dataCompetencia: form.dataCompetencia,
                funcionarioFolha: form.funcionarioFolha

            });

            pushInfoNotification('Sucesso', 'Configurações salvas com sucesso!', { isAutoFinish: true });
        } catch (e: unknown) {
            pushErrorNotification('Error', processError(e, 'Falha ao carregar filtros do relatório!'));
        } finally {
            setLoading(false);
        }
    }, [
        saveContasPagarAnaliticoFiltrosUseCase,
        pushErrorNotification,
        pushInfoNotification
    ]);


    const loadContasPagarRelatorioFiltros = useCallback(async () => {
        try {
            const filtros = await getContasPagarAnaliticoFiltrosUseCase.execute();
            setFiltroContasPagarAnalitico(filtros ?? undefined);
        } catch (e: unknown) {
            setFiltroContasPagarAnalitico(undefined);
            throw e;
        }
    }, [
        getContasPagarAnaliticoFiltrosUseCase,
    ]);

    const handleOpenReportContasPagarAnaliticoModal = useCallback(async (openState: boolean) => {
        setOpenReportContasPagarAnaliticoModal(openState);
        try {
            setLoading(true);
            if (openState) {
                await loadContasPagarRelatorioFiltros();
                setOpenReportContasPagarAnaliticoModal(openState);
            } else {
                setOpenReportContasPagarAnaliticoModal(openState);
            }

        } catch (e: unknown) {
            pushErrorNotification('Error', processError(e, 'Falha ao carregar filtros do relatório!'));
        }
        finally {
            setLoading(false);
        }
    }, [
        loadContasPagarRelatorioFiltros,
        pushErrorNotification,
    ]);


    //#endregion




    //#region PAGINAÇÃO.

    const handleReload = useCallback(async () => {
        try {
            setLoading(true);
            await loadContasPagarListaState();
        } catch (e: unknown) {
            pushErrorNotification('Error', processError(e, 'Falha ao buscar lista de contas a pagar!'));
        } finally {
            setLoading(false);
        }
    }, [loadContasPagarListaState]);

    const handleRowSelectionModelChange = useCallback((rows: GridRowSelectionModel) => {
        setRowSelectionModel(rows);
    }, []);

    const handlePageChange = useCallback(async (page: number) => {
        try {
            setLoading(true);
            pagination.setPage(page + 1);
            await loadContasPagarListaState({ page: page + 1 });
        } catch (e: unknown) {
            pushErrorNotification('Error', processError(e, 'Falha ao buscar lista de contas a pagar!'));
        } finally {
            setLoading(false);
        }
    }, [pagination, loadContasPagarListaState]);

    const handleRowsPerPageChange = useCallback(async (rowsPerPage: number) => {
        try {
            setLoading(true);
            pagination.setPage(1);
            pagination.setRowsPerPage(rowsPerPage);
            await loadContasPagarListaState({ rowsPerPage, page: 1 });
        } catch (e: unknown) {
            pushErrorNotification('Error', processError(e, 'Falha ao buscar contas a pagar!'));
        } finally {
            setLoading(false);
        }
    }, [pagination, loadContasPagarListaState]);

    //#endregion




    //#region DELECAO
    const handleContasPagarDeleteOpenState = useCallback((idContasPagar: number | null, openState: boolean) => {
        if (openState) {
            if (idContasPagar && contasPagarLista) {
                setSelectedIdContasPagar(contasPagarLista.find(x => x.idContasPagar === idContasPagar)?.idContasPagar);
            } else {
                setSelectedIdContasPagar(undefined);
            }
        }
        setOpenDeleteModal(openState);
    }, [contasPagarLista]);

    const handleConfirmarDeletarContasPagar = useCallback(async () => {
        try {

            setSaving(true);
            if (selectedIdContasPagar) {
                await deleteContasPagarUseCase.deleteContasPagarByIds([selectedIdContasPagar], "lista");
                pushInfoNotification("Sucesso", "Conta deletada com sucesso!", { isAutoFinish: true });

            } else {
                if (rowSelectionModel) {
                    const idContasPagar: number[] = [];
                    for (let row of rowSelectionModel.ids.values()) {
                        if (typeof row === 'number') {
                            idContasPagar.push(row);
                        } else if (typeof row === 'string') {
                            try {
                                idContasPagar.push(Number(row));
                            } catch (e) {
                            }
                        }
                    }
                    await deleteContasPagarUseCase.deleteContasPagarByIds(idContasPagar, "lista");
                    pushInfoNotification("Sucesso", "Conta deletada com sucesso!", { isAutoFinish: true });
                }
            }

            await loadContasPagarListaState();

        } catch (e: unknown) {
            pushErrorNotification("Erro", processError(e, "Falha ao deletar conta!"), { isAutoFinish: true });
        } finally {
            setSaving(false);
            setOpenDeleteModal(false);
            setSelectedIdContasPagar(undefined)
            setRowSelectionModel({ type: 'include', ids: new Set() });
        }
    }, [
        selectedIdContasPagar,
        deleteContasPagarUseCase,
        loadContasPagarListaState,
        rowSelectionModel,
        pushErrorNotification,
        pushInfoNotification]);


    //#endregion




    //#region PESQUISA.

    const handleIdCentroCustoChange = useDebouncedCallback(
        debouncedRef,
        (newValues: number[]) => {
            filters.setFilters({ idsCentroCusto: newValues })
            return async () => {
                try {
                    setLoading(true);
                    pagination.setPage(1);
                    await loadContasPagarListaState({ idsCentroCusto: newValues, page: 1 });
                } catch (e: unknown) {
                    pushErrorNotification("Error", processError(e, "Falha ao buscar contas a pagar!"));
                } finally {
                    setLoading(false);
                }
            };
        }, [filters,
        pagination,
        loadContasPagarListaState,]
    );

    const handleIdBancoChange = useDebouncedCallback(
        debouncedRef,
        (newValues: number[]) => {
            filters.setFilters({ idsBanco: newValues })
            return async () => {
                try {
                    setLoading(true);
                    pagination.setPage(1);
                    await loadContasPagarListaState({ idsBanco: newValues, page: 1 });
                } catch (e: unknown) {
                    pushErrorNotification("Error", processError(e, "Falha ao buscar contas a pagar!"));
                } finally {
                    setLoading(false);
                }
            };
        }, [filters,
        pagination,
        loadContasPagarListaState,]
    );

    const handleIdOrdemCompraChange = useDebouncedCallback(
        debouncedRef,
        (newValues: number[]) => {
            filters.setFilters({ idsOrdemCompra: newValues })
            return async () => {
                try {
                    setLoading(true);
                    pagination.setPage(1);
                    await loadContasPagarListaState({ idsOrdemCompra: newValues, page: 1 });
                } catch (e: unknown) {
                    pushErrorNotification("Error", processError(e, "Falha ao buscar contas a pagar!"));
                } finally {
                    setLoading(false);
                }
            };
        }, [filters,
        pagination,
        loadContasPagarListaState,]
    );

    const handleIdSubcontaChange = useDebouncedCallback(
        debouncedRef,
        (newValues: number[]) => {
            filters.setFilters({ idsSubconta: newValues })
            return async () => {
                try {
                    setLoading(true);
                    pagination.setPage(1);
                    await loadContasPagarListaState({ idsSubconta: newValues, page: 1 });
                } catch (e: unknown) {
                    pushErrorNotification("Error", processError(e, "Falha ao buscar contas a pagar!"));
                } finally {
                    setLoading(false);
                }
            };
        }, [filters,
        pagination,
        loadContasPagarListaState,]
    );


    const handleTituloChange = useDebouncedCallback(
        debouncedRef,
        (titulo: string | null) => {
            filters.setFilters({ titulo });
            return async () => {
                try {
                    setLoading(true);
                    pagination.setPage(1);
                    await loadContasPagarListaState({ titulo, page: 1 });
                } catch (e: unknown) {
                    pushErrorNotification("Error", processError(e, "Falha ao buscar contas a pagar!"));
                } finally {
                    setLoading(false);
                }
            };
        },
        [loadContasPagarListaState, filters]
    );

    const handleDataPagoRangeChange = useDebouncedCallback(
        debouncedRef,
        (dataPagoDe: Date | null, dataPagoAte: Date | null) => {
            filters.setFilters({ dataPagoDe });
            filters.setFilters({ dataPagoAte });

            return async () => {
                try {
                    setLoading(true);
                    pagination.setPage(1);
                    await loadContasPagarListaState({ dataPagoDe, dataPagoAte, page: 1 });
                } catch (e: unknown) {
                    pushErrorNotification("Error", processError(e, "Falha ao buscar contas a pagar!"));
                } finally {
                    setLoading(false);
                }
            };
        },
        [loadContasPagarListaState, filters, pagination]
    );

    const handleDataPagarRangeChange = useDebouncedCallback(
        debouncedRef,
        (dataPagarDe: Date | null, dataPagarAte: Date | null) => {
            filters.setFilters({ dataPagarDe });
            filters.setFilters({ dataPagarAte });

            return async () => {
                try {
                    setLoading(true);
                    pagination.setPage(1);
                    await loadContasPagarListaState({ dataPagarDe, dataPagarAte, page: 1 });
                } catch (e: unknown) {
                    pushErrorNotification("Error", processError(e, "Falha ao buscar contas a pagar!"));
                } finally {
                    setLoading(false);
                }
            };
        },
        [loadContasPagarListaState, filters, pagination]
    );

    const handleDataCriacaoRangeChange = useDebouncedCallback(
        debouncedRef,
        (dataCriacaoDe: Date | null, dataCriacaoAte: Date | null) => {
            filters.setFilters({ dataCriacaoDe });
            filters.setFilters({ dataCriacaoAte });
            return async () => {
                try {
                    setLoading(true);
                    pagination.setPage(1);
                    await loadContasPagarListaState({ dataCriacaoDe, dataCriacaoAte, page: 1 });
                } catch (e: unknown) {
                    pushErrorNotification("Error", processError(e, "Falha ao buscar contas a pagar!"));
                } finally {
                    setLoading(false);
                }
            };
        },
        [loadContasPagarListaState, filters]
    );

    const handleStatusChange = useDebouncedCallback(debouncedRef, (status: number | null) => {
        useContasPagarListaFilters.setState({ status })
        return async () => {
            try {
                setLoading(true);
                pagination.setPage(1);
                await loadContasPagarListaState({ status, page: 1 });
            } finally {
                setLoading(false);
            }
        };
    }, [loadContasPagarListaState]);


    const handleClearFilters = useCallback(async () => {
        try {
            setLoading(true);
            filters.clearFilters();
            pagination.setPage(1);
            await loadContasPagarListaState({
                ...initialContasPagarFiltersValues,
                page: 1
            });
        } catch (e: unknown) {
            pushErrorNotification("Erro", processError(e, 'Falha ao buscar contas a pagar!'));
        } finally {
            setLoading(false);
        }
    }, [filters, pagination, loadContasPagarListaState, pushErrorNotification]);


    const handleIdFornecedorChange = useDebouncedCallback(
        debouncedRef,
        (newValues: number[]) => {
            filters.setFilters({ idsFornecedor: newValues });
            return async () => {
                try {
                    setLoading(true);
                    pagination.setPage(1);
                    await loadContasPagarListaState({ idsFornecedor: newValues, page: 1 });
                } catch (e: unknown) {
                    pushErrorNotification("Erro", processError(e, "Falha ao buscar contas a pagar!"));
                } finally {
                    setLoading(false);
                }
            };
        },
        [filters, pagination, loadContasPagarListaState]
    );

    const handleIdFuncionarioChange = useDebouncedCallback(
        debouncedRef,
        (newValues: number[]) => {
            filters.setFilters({ idsFuncionario: newValues });
            return async () => {
                try {
                    setLoading(true);
                    pagination.setPage(1);
                    await loadContasPagarListaState({ idsFuncionario: newValues, page: 1 });
                } catch (e: unknown) {
                    pushErrorNotification("Erro", processError(e, "Falha ao buscar contas a pagar!"));
                } finally {
                    setLoading(false);
                }
            };
        },
        [filters, pagination, loadContasPagarListaState]
    );


    const handleIdFuncionarioPagoChange = useDebouncedCallback(
        debouncedRef,
        (newValues: number[]) => {
            filters.setFilters({ idsFuncionarioPago: newValues });
            return async () => {
                try {
                    setLoading(true);
                    pagination.setPage(1);
                    await loadContasPagarListaState({ idsFuncionarioPago: newValues, page: 1 });
                } catch (e: unknown) {
                    pushErrorNotification("Erro", processError(e, "Falha ao buscar contas a pagar!"));
                } finally {
                    setLoading(false);
                }
            };
        },
        [filters, pagination, loadContasPagarListaState]
    );

    const handleIdProdutoServicoChange = useDebouncedCallback(
        debouncedRef,
        (newValues: number[]) => {
            filters.setFilters({ idsProdutoServico: newValues });
            return async () => {
                try {
                    setLoading(true);
                    pagination.setPage(1);
                    await loadContasPagarListaState({ idsProdutoServico: newValues, page: 1 });
                } catch (e: unknown) {
                    pushErrorNotification("Erro", processError(e, "Falha ao buscar contas a pagar!"));
                } finally {
                    setLoading(false);
                }
            };
        },
        [filters, pagination, loadContasPagarListaState]
    );

    const handleIdPgtoMedicaoChange = useDebouncedCallback(
        debouncedRef,
        (newValues: number[]) => {
            filters.setFilters({ idsPgtoMedicao: newValues });
            return async () => {
                try {
                    setLoading(true);
                    pagination.setPage(1);
                    await loadContasPagarListaState({ idsPgtoMedicao: newValues, page: 1 });
                } catch (e: unknown) {
                    pushErrorNotification("Erro", processError(e, "Falha ao buscar contas a pagar!"));
                } finally {
                    setLoading(false);
                }
            };
        },
        [filters, pagination, loadContasPagarListaState]
    );

    const handleValorChange = useDebouncedCallback(
        debouncedRef,
        (newValue: number | null) => {
            const valorNormalizado =
                newValue !== null && !isNaN(Number(newValue)) ? Number(newValue) : null;

            filters.setFilters({ valor: valorNormalizado });
            return async () => {
                try {
                    setLoading(true);
                    pagination.setPage(1);
                    await loadContasPagarListaState({ valor: valorNormalizado, page: 1 });
                } catch (e: unknown) {
                    pushErrorNotification("Erro", processError(e, "Falha ao buscar contas a pagar!"));
                } finally {
                    setLoading(false);
                }
            };
        },
        [filters, pagination, loadContasPagarListaState]
    );

    const handleNrContaChange = useDebouncedCallback(
        debouncedRef,
        (newValue: string | null) => {
            const valorNormalizado =
                newValue && !isNaN(Number(newValue)) && newValue.trim() !== ""
                    ? Number(newValue)
                    : null;

            filters.setFilters({ nrContasPagar: valorNormalizado });
            return async () => {
                try {
                    setLoading(true);
                    pagination.setPage(1);
                    await loadContasPagarListaState({ nrContasPagar: valorNormalizado, page: 1 });
                } catch (e: unknown) {
                    pushErrorNotification("Erro", processError(e, "Falha ao buscar contas a pagar!"));
                } finally {
                    setLoading(false);
                }
            };
        },
        [filters, pagination, loadContasPagarListaState]
    );

    const handleNotaFiscalChange = useDebouncedCallback(
        debouncedRef,
        (newValue: string | null) => {
            filters.setFilters({ nrNotaFiscal: newValue });
            return async () => {
                try {
                    setLoading(true);
                    pagination.setPage(1);
                    await loadContasPagarListaState({ nrNotaFiscal: newValue, page: 1 });
                } catch (e: unknown) {
                    pushErrorNotification("Erro", processError(e, "Falha ao buscar contas a pagar!"));
                } finally {
                    setLoading(false);
                }
            };
        },
        [filters, pagination, loadContasPagarListaState]
    );

    const handleNotaFiscalEspecialChange = useDebouncedCallback(
        debouncedRef,
        (newValue: string | null) => {
            filters.setFilters({ nrNotaFiscalEspecial: newValue });
            return async () => {
                try {
                    setLoading(true);
                    pagination.setPage(1);
                    await loadContasPagarListaState({ nrNotaFiscalEspecial: newValue, page: 1 });
                } catch (e: unknown) {
                    pushErrorNotification("Erro", processError(e, "Falha ao buscar contas a pagar!"));
                } finally {
                    setLoading(false);
                }
            };
        },
        [filters, pagination, loadContasPagarListaState]
    );

    const handleFormaPagamentoChange = useDebouncedCallback(
        debouncedRef,
        (newValue: string | null) => {
            filters.setFilters({ formaPagamento: newValue });
            return async () => {
                try {
                    setLoading(true);
                    pagination.setPage(1);
                    await loadContasPagarListaState({ formaPagamento: newValue, page: 1 });
                } catch (e: unknown) {
                    pushErrorNotification("Erro", processError(e, "Falha ao buscar contas a pagar!"));
                } finally {
                    setLoading(false);
                }
            };
        },
        [filters, pagination, loadContasPagarListaState]
    );

    //#endregion 




    //#region COPIA/EXPORTAÇÃO.


    const handleContasPagarExportar = useCallback(async (idContapagar: number) => {
        try {
            setLoading(true);
            await startReportViewerProcess({
                fetchCallback: async (mimeType) => {
                    return await getReportContaPagarUseCase.execute(idContapagar, mimeType);
                }
            });

        } catch (e: unknown) {
            pushErrorNotification("Erro", processError(e, 'Falha ao gerar relatório de Conta a pagar!'));
        } finally {
            setLoading(false);
        }
    }, [
        getReportContaPagarUseCase,
        pushErrorNotification,
        startReportViewerProcess
    ]);


    const handleContasPagarCopiaOpenState = useCallback((idContasPagar: number | null, openState: boolean) => {
        if (openState) {
            if (idContasPagar && contasPagarLista) {
                setSelectedIdContaPagarCopia(idContasPagar);
                settituloContasPagarCopia(contasPagarLista.find(x => x.idContasPagar === idContasPagar)?.titulo ?? '');
                setOpenCopiaContasPagarModal(true);
            } else {
                setSelectedIdContaPagarCopia(undefined);
            }
        }
    }, [contasPagarLista]);

    const handleSubmitContaPagarCopia = useCallback(async (form: ContasPagarCopiaForm) => {

        try {
            if (selectedIdContaPagarCopia) {
                setSaving(true);

                await copiaContasPagarUseCase.copiaContasPagar({
                    ...form
                });

                pushInfoNotification("Sucesso", "Conta a pagar copiada com sucesso!", { isAutoFinish: true });
                await loadContasPagarListaState();

            } else {
                pushWarnNotification("Atenção", "Nenhuma conta a pagar selecionada para cópia!");
                return;
            }
        } catch (e: unknown) {
            pushErrorNotification("Erro", processError(e, "Falha ao copiar Conta a pagar!"));
        } finally {
            setOpenCopiaContasPagarModal(false);
            setSelectedIdContaPagarCopia(undefined);
            settituloContasPagarCopia('');
            setSaving(false);
        }
    }, [
        selectedIdContaPagarCopia,
        copiaContasPagarUseCase,
        loadContasPagarListaState,
        pushErrorNotification,
        pushInfoNotification,
        pushWarnNotification,

    ]);

    //#endregion




    //#region PAGAMENTO
    const calculaTotalRecebido = useCallback(() => {
        if (rowSelectionModel && contasPagarLista) {
            let total = 0;
            for (let row of rowSelectionModel.ids.values()) {
                let idRow: number | null = null;
                if (typeof row === 'number') {
                    idRow = row;
                } else if (typeof row === 'string') {
                    try {
                        idRow = Number(row);
                    } catch (e) {
                    }
                }
                if (idRow) {
                    const conta = contasPagarLista.find(x => x.idContasPagar === idRow);
                    if (conta) {
                        total += conta.valorPagar ?? 0;;
                    }
                }
            }
            setValorTotalRecebido(total);
        } else {
            setValorTotalRecebido(0);
        }
    }, [rowSelectionModel, contasPagarLista]);

    const handleOpenCadastroPagamentoModal = useCallback((openState: boolean) => {
        if (openState) {
            if (!rowSelectionModel || rowSelectionModel.ids.size === 0) {
                pushWarnNotification("Atenção", "Selecione as contas a serem recebidas!");
                return;
            }
        }
        calculaTotalRecebido();
        setOpenCadastroPagamentoModal(openState);
    }, [
        rowSelectionModel,
        pushWarnNotification,
        calculaTotalRecebido,
    ]);



    const handleCadastroPagamentoSubmit = useCallback(async (form: ContasPagamentoForm) => {
        try {

            setSaving(true);
            if (rowSelectionModel) {

                const idContasPagar: number[] = [];
                for (let row of rowSelectionModel.ids.values()) {
                    if (typeof row === 'number') {
                        idContasPagar.push(row);
                    } else if (typeof row === 'string') {
                        try {
                            idContasPagar.push(Number(row));
                        } catch (e) {
                        }
                    }
                }
                await pagamentoContasUseCase.pagamentoContas({
                    ...form,
                    idsContasPagar: idContasPagar
                })
                pushInfoNotification("Sucesso", "Contas Recebidas com sucesso!", { isAutoFinish: true });
                await loadContasPagarListaState();

            }
        } catch (e: unknown) {
            pushErrorNotification("Erro", processError(e, "Falha ao pagar Contas!"));
        } finally {
            setSaving(false);
            setOpenCadastroPagamentoModal(false);
            setRowSelectionModel({ type: 'include', ids: new Set() });
        }
    }, [
        rowSelectionModel,
        pagamentoContasUseCase,
        loadContasPagarListaState,
        pushErrorNotification,
        pushInfoNotification,
    ]);

    //#endregion

    return {
        loading,
        saving,
        errorMessage,
        openCopiaContasPagarModal,
        tituloContasPagarCopia,
        contasPagarLista: contasPagarLista ?? [],
        valorTotalRecebido,
        openDeleteModal,
        openCadastroPagamentoModal,
        rowSelectionModel,
        filters,
        pagination,
        ddlCentroCusto: ddlCentroCusto ?? [],
        ddlBanco: ddlBanco ?? [],
        ddlOrdemCompra: ddlOrdemCompra ?? [],
        ddlSubconta: ddlSubconta ?? [],
        ddlFornecedor: ddlFornecedor ?? [],
        ddlFuncionario: ddlFuncionario ?? [],
        ddlProdutoServico: ddlProdutoServico ?? [],
        ddlPgtoMedicao: ddlPgtoMedicao ?? [],
        selectedIdContasPagar,
        selectedIdContaPagarCopia,
        reportViewerState,
        filtroContasPagarAnalitico,
        openReportContasPagarAnaliticoModal,
        valoresContasMes,

        handleIdFuncionarioPagoChange,
        handleReportContasPagarAnaliticoColumnsSubmit,
        handleReportContasPagarAnaliticoSubmit,
        handleOpenReportContasPagarAnaliticoModal,
        setOpenCopiaContasPagarModal,
        setOpenCadastroPagamentoModal,
        handleContasPagarExportar,
        handleContasPagarCopiaOpenState,
        handleValorChange,
        handleNrContaChange,
        handleNotaFiscalChange,
        handleNotaFiscalEspecialChange,
        handleFormaPagamentoChange,
        handleContasPagarEdit,
        handleNewContasPagar,
        handleContasPagarDeleteOpenState,
        handleConfirmarDeletarContasPagar,
        handleSubmitContaPagarCopia,
        handleReload,
        handlePageChange,
        handleRowsPerPageChange,
        handleStatusChange,
        handleClearFilters,
        handleRowSelectionModelChange,
        handleIdCentroCustoChange,
        handleIdBancoChange,
        handleIdOrdemCompraChange,
        handleIdSubcontaChange,
        handleIdFornecedorChange,
        handleIdFuncionarioChange,
        handleIdProdutoServicoChange,
        handleIdPgtoMedicaoChange,
        handleTituloChange,
        handleDataPagarRangeChange,
        handleDataPagoRangeChange,
        handleDataCriacaoRangeChange,
        handleOpenCadastroPagamentoModal,
        handleCadastroPagamentoSubmit,

    };

}
